import { queryOptions, useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { SafeLink } from "@/components/layout/SafeLink";
import { Skeleton } from "@/components/ui/skeleton";
import { ShieldCheck, CaretRight as ChevronRight, Package } from "@phosphor-icons/react";
import { useCountry } from "@/hooks/useCountry";
import { dealerProfilePath } from '@/utils/dealerUrl';

export const trustedDealersQueryOptions = (country: string) =>
  queryOptions({
    queryKey: ["homepage-trusted-dealers", country],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_verified_dealers_public', { p_country_code: country });
      if (error) throw error;
      const ids = (data || []).map((d) => d.user_id);
      const { data: profiles } = ids.length ? await supabase.from('dealer_profiles').select('user_id, verified, risco_verified, subdomain_alias').in('user_id', ids) : { data: [] };
      const verifiedIds = new Set((profiles || []).filter((p) => p.verified === true || p.risco_verified === true).map((p) => p.user_id));
      // Sort by listing count desc, take top 3
      return (data || [])
        .filter((d) => verifiedIds.has(d.user_id))
        .sort((a: any, b: any) => (b.listing_count || 0) - (a.listing_count || 0))
        .slice(0, 3)
        .map((d: any) => ({
          user_id: d.user_id,
          dealer_id: d.dealer_id,
          canonical_path: dealerProfilePath(profiles?.find(p => p.user_id === d.user_id) ?? {}),
          business_name: d.business_name || d.email?.split('@')[0]?.replace(/[._-]/g, ' ')?.replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Dealer',
          listing_count: Number(d.listing_count) || 0,
          verified_since: d.verified_since,
          is_accessible: d.is_active_subscriber === true,
          logo_url: d.logo_url || null,
        }));
    },
    staleTime: 5 * 60 * 1000,
  });

export default function TrustedDealers() {
  const { country } = useCountry();

  const { data: dealers, isLoading } = useQuery(trustedDealersQueryOptions(country));


  if (isLoading) {
    return (
      <section className="max-w-6xl mx-auto px-4 py-8">
        <Skeleton className="h-8 w-64 mb-6" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-44 rounded-xl" />
          ))}
        </div>
      </section>
    );
  }

  if (!dealers?.length) return null;

  const t = {
    RO: { title: 'Dealeri verificați pe RO Auto Market', verified: 'Dealer Verificat', stock: 'vehicule în stoc', cta: 'Vezi Oferte', all: 'Vezi toți dealerii verificați →' },
    UK: { title: 'Verified dealers on RO Auto Market', verified: 'Verified Dealer', stock: 'vehicles in stock', cta: 'View Offers', all: 'See all verified dealers →' },
    DE: { title: 'Verifizierte Händler auf RO Auto Market', verified: 'Verifizierter Händler', stock: 'Fahrzeuge auf Lager', cta: 'Angebote ansehen', all: 'Alle verifizierten Händler ansehen →' },
  }[country] || { title: 'Dealeri verificați pe RO Auto Market', verified: 'Dealer Verificat', stock: 'vehicule în stoc', cta: 'Vezi Oferte', all: 'Vezi toți dealerii verificați →' };

  return (
    <section className="max-w-6xl mx-auto px-4 py-8">
      <h2 className="text-xl md:text-2xl font-bold text-primary text-center mb-6">
        {t.title}
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {dealers.map((dealer) => {
          const initial = dealer.business_name.charAt(0).toUpperCase();
          const link = dealer.canonical_path || `/vehicles?dealer=${dealer.user_id}`;

          return (
            <SafeLink
              key={dealer.user_id}
              to={link}
              className="group block rounded-xl border border-border bg-card shadow-xs hover:shadow-md transition-all hover:border-primary/30 overflow-hidden"
            >
              {/* Top accent bar */}
              <div className="h-1.5 bg-gradient-to-r from-primary to-primary/60" />

              <div className="p-5">
                {/* Avatar + name */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 border border-primary/20 group-hover:bg-primary/20 transition-colors overflow-hidden">
                    {dealer.logo_url ? (
                      <img
                        src={dealer.logo_url}
                        alt={`Logo ${dealer.business_name}`}
                        loading="lazy"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          const img = e.currentTarget as HTMLImageElement;
                          img.style.display = "none";
                          const sibling = img.nextElementSibling as HTMLElement | null;
                          if (sibling) sibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <span
                      className="w-full h-full items-center justify-center text-xl font-black text-primary"
                      style={{ display: dealer.logo_url ? "none" : "flex" }}
                    >
                      {initial}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-foreground text-base truncate group-hover:text-primary transition-colors">
                      {dealer.business_name}
                    </h3>
                    <div className="flex items-center gap-1 mt-0.5">
                      <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                      <span className="text-xs font-medium text-primary">{t.verified}</span>
                    </div>
                  </div>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                  <Package className="h-4 w-4" />
                  <span><strong className="text-foreground">{dealer.listing_count}</strong> {t.stock}</span>
                </div>

                {/* CTA — outlined brand-green for trust */}
                <div className="flex justify-center">
                  <span className="inline-flex items-center gap-1 border border-brand-green text-brand-green bg-transparent px-5 py-2 rounded-xl text-sm font-semibold group-hover:bg-brand-green group-hover:text-brand-green-foreground transition-colors">
                    {t.cta} <ChevronRight className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </SafeLink>
          );
        })}
      </div>

      <div className="text-center mt-5">
        <SafeLink
          to="/dealeri-verificati"
          className="text-sm font-semibold text-primary hover:underline"
        >
          {t.all}
        </SafeLink>
      </div>
    </section>
  );
}
